import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { useAuth } from '../contexts/AuthContext';
import { ROLES, canAccessAdmin, canAccessCustomerWalletBranding, canAccessLocationWalletPortal, canAccessMembersManagement, canAccessSupportSettings, canAccessKonkr } from '../utils/accessControl';
import activeAccessApi from '../api/activeAccessApi';
import walletApi from '../api/walletApi';
import { canEnterFlagSystem } from '../utils/flagSystem';
import { useCustomerFlagSystemCapability } from '../contexts/FlagSystemCapabilityContext';
import { FlagCapabilityState } from './flags/FlagSystemCommon';

const SupportNavbar = () => {
	const navigate = useNavigate();
	const supportPage = useMatch('/support/:section/*');
	const isFlagSystemPage = Boolean(supportPage &&
		['flags', 'local-users', 'site-viewer'].includes(supportPage.params.section));
	const menuRef = useRef(null);
	const { accessContext, isAuthReady, isLoggedIn, logout } = useAuth();
	const role = accessContext && accessContext.role;
	const isAdmin = canAccessAdmin(accessContext);
	const isCustomerWalletUser = canAccessCustomerWalletBranding(accessContext);
	const isLocationWalletUser = canAccessLocationWalletPortal(accessContext);
	const hasSettingsAccess = canAccessSupportSettings(accessContext);
	const hasMembersAccess = canAccessMembersManagement(accessContext);
	const hasKonkrAccess = isAuthReady && isLoggedIn && canAccessKonkr(accessContext);
	const isKonkrUser = role === ROLES.KonkrUser;
	const isSiteUser = [ROLES.SeniorLocalUser, ROLES.DutyManager, ROLES.JuniorLocalUser].includes(role);
	const flagCapability = useCustomerFlagSystemCapability();
	const hasFlagAccess = isAuthReady && isLoggedIn && canEnterFlagSystem(accessContext) &&
		(role !== 'CustomerLevelUser' || flagCapability.enabled);
	const [walletAvailable, setWalletAvailable] = useState(false);
	const [customerWalletManager, setCustomerWalletManager] = useState(false);
	const [activeAccessBrandingAvailable, setActiveAccessBrandingAvailable] = useState(false);
	const customerId = Number(accessContext && accessContext.claims && accessContext.claims.customerId);
	const locationId = Number(accessContext && accessContext.claims && accessContext.claims.locationId);

	useEffect(() => {
		let active = true;
		const loadAvailability = async () => {
			if (isCustomerWalletUser && Number.isFinite(customerId) && customerId > 0) {
				try {
					await walletApi.getSupportCustomerSettings(customerId);
					if (active) setCustomerWalletManager(true);
				} catch (_) {
					if (active) setCustomerWalletManager(false);
				}
			} else if (active) {
				setCustomerWalletManager(false);
			}

			if (!isLocationWalletUser) {
				if (active) setWalletAvailable(false);
				return;
			}

			if (!Number.isFinite(locationId) || locationId <= 0) {
				if (active) setWalletAvailable(false);
				return;
			}

			try {
				const result = await walletApi.getLocationAvailability(locationId);
				if (active) setWalletAvailable(Boolean(result && result.isEnabled));
			} catch (_) {
				if (active) setWalletAvailable(false);
			}
		};
		loadAvailability();
		return () => { active = false; };
	}, [customerId, isCustomerWalletUser, isLocationWalletUser, locationId]);

	useEffect(() => {
		let active = true;
		const loadActiveAccessAvailability = async () => {
			if (!isCustomerWalletUser || !Number.isFinite(customerId) || customerId <= 0) {
				if (active) {
					setActiveAccessBrandingAvailable(false);
				}
				return;
			}

			try {
				await activeAccessApi.getActiveAccessPortalSummary(customerId);
				if (active) {
					setActiveAccessBrandingAvailable(true);
				}
			} catch (_) {
				if (active) {
					setActiveAccessBrandingAvailable(false);
				}
			}
		};
		loadActiveAccessAvailability();
		return () => { active = false; };
	}, [customerId, isCustomerWalletUser]);

	const navigateFromMenu = (path) => {
		if (menuRef.current) {
			const fallbackTarget = document.activeElement || document.body;
			menuRef.current.hide({ currentTarget: fallbackTarget });
		}

		window.setTimeout(() => {
			navigate(path);
		}, 0);
	};

	const openWalletPortal = () => {
		if (menuRef.current) {
			const fallbackTarget = document.activeElement || document.body;
			menuRef.current.hide({ currentTarget: fallbackTarget });
		}

		window.open('/support/wallet', '_blank', 'noopener,noreferrer');
	};

	const logoutUser = async () => {
		await logout();
		navigate('/login');
	};

	const items = useMemo(() => {
		const next = [
			{
				label: 'Account',
				icon: 'pi pi-user',
				command: () => navigateFromMenu('/support/account'),
			},
		];

		if (!isKonkrUser && !isSiteUser) {
			next.push({
				label: 'Resources',
				icon: 'pi pi-folder',
				command: () => navigateFromMenu('/support/resources'),
			});
		}

		if (!isKonkrUser && hasSettingsAccess) {
			next.push({
				label: 'Settings',
				icon: 'pi pi-cog',
				command: () => navigateFromMenu('/support/settings'),
			});
		}

		if (hasMembersAccess) {
			next.push({
				label: 'Members',
				icon: 'pi pi-users',
				command: () => navigateFromMenu('/support/members'),
			});
		}

		if (hasKonkrAccess) {
			next.push({ label: 'Konkr Rewards', icon: 'pi pi-gift', command: () => navigateFromMenu('/support/konkr/advertisers') });
		}

		if (hasFlagAccess) {
			if (role === 'CustomerLevelUser') {
				next.push(
					{ label: 'Flag Management', icon: 'pi pi-flag', command: () => navigateFromMenu('/support/flags') },
					{ label: 'Local User Management', icon: 'pi pi-users', command: () => navigateFromMenu('/support/local-users') }
				);
			}
			next.push({ label: 'Site Viewer', icon: 'pi pi-desktop', command: () => navigateFromMenu('/support/site-viewer') });
		}

		if (customerWalletManager) {
			next.push({
				label: 'Wallet Passes',
				icon: 'pi pi-wallet',
				command: () => navigateFromMenu('/support/wallet-branding'),
			});
		}

		if (activeAccessBrandingAvailable && isCustomerWalletUser) {
			next.push({
				label: 'Active Access Branding',
				icon: 'pi pi-palette',
				command: () => navigateFromMenu('/support/active-access-branding'),
			});
		}

		if (walletAvailable && isLocationWalletUser) {
			next.push({
				label: 'Open Wallet Portal',
				icon: 'pi pi-wallet',
				command: openWalletPortal,
			});
		}

		if (isAdmin) {
			next.push(
				{ separator: true },
				{
					label: 'Admin Users',
					icon: 'pi pi-users',
						command: () => navigateFromMenu('/admin/users'),
				},
				{
					label: 'Admin Features',
					icon: 'pi pi-sliders-h',
						command: () => navigateFromMenu('/admin/features'),
				}
			);
		}

		next.push(
			{ separator: true },
			{
				label: 'Logout',
				icon: 'pi pi-sign-out',
				command: logoutUser,
			}
		);

		return next;
	}, [navigate, logoutUser, isAdmin, hasSettingsAccess, hasMembersAccess, walletAvailable, customerWalletManager, activeAccessBrandingAvailable, isCustomerWalletUser, isLocationWalletUser, hasFlagAccess, hasKonkrAccess, isKonkrUser, accessContext]);

	return (
		<div className="panel app-navbar" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
			<div className="flex align-items-center justify-content-between">
				<Button
					label="Smart Tile Support"
					className="p-button-text"
					aria-label="Smart Tile Support home"
					onClick={() => navigate('/')}
				/>
				<div className="flex align-items-center">
					<Button
						icon="pi pi-bars"
						className="p-button-text"
						aria-label="Support menu"
						onClick={(e) => menuRef.current && menuRef.current.toggle(e)}
					/>
					<Menu model={items} popup ref={menuRef} />
				</div>
			</div>
			{isCustomerWalletUser && !isFlagSystemPage && ['idle', 'loading', 'error'].includes(flagCapability.status) &&
				<FlagCapabilityState capability={flagCapability} />}
		</div>
	);
};

export default SupportNavbar;
